import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Building2, Home, TestTube2, ChevronRight } from 'lucide-react';
import { patientApi } from '../api';

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: 'bg-yellow-100 text-yellow-700',
  ACCEPTED: 'bg-blue-100 text-blue-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  SAMPLE_COLLECTED: 'bg-indigo-100 text-indigo-700',
  IN_PROGRESS: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REPORT_READY: 'bg-green-100 text-green-700',
};

export default function PatientLabAppointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    patientApi.lab.getAppointments()
      .then((d: any) => { if (alive) setAppointments(Array.isArray(d) ? d : d?.appointments || []); })
      .catch(() => { if (alive) setAppointments([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Lab Appointments</h1>
        <p className="text-sm text-gray-500 mt-1">Your lab bookings</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-16">
          <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No appointments yet</p>
          <p className="text-sm text-gray-400 mt-1">Book a lab test to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((apt: any) => {
            const status = apt.status || 'REQUESTED';
            return (
              <button
                key={apt.id || apt._id}
                onClick={() => navigate(`/patient/lab-appointments/${apt.id || apt._id}`)}
                className="w-full bg-white rounded-xl border border-gray-200 p-5 text-left hover:shadow-md hover:border-teal-200 transition-all group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-400">{apt.appointmentNumber || apt.id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                        {status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{apt.labName || 'Lab'}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {apt.date || apt.slotDate || '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        {apt.collectionType === 'HOME_COLLECTION' ? <Home className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                        {apt.collectionType === 'HOME_COLLECTION' ? 'Home' : 'Lab Visit'}
                      </span>
                      <span className="flex items-center gap-1">
                        <TestTube2 className="w-3.5 h-3.5" />
                        {apt.testCount || apt.tests?.length || 0} tests
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-teal-500 shrink-0 mt-1 transition-colors" />
                </div>

                {/* Status dots */}
                <div className="flex items-center gap-1 mt-3">
                  {['REQUESTED', 'ACCEPTED', 'SCHEDULED', 'SAMPLE_COLLECTED', 'COMPLETED'].map((s, i) => {
                    const allStatuses = ['REQUESTED', 'ACCEPTED', 'SCHEDULED', 'SAMPLE_COLLECTED', 'COMPLETED'];
                    const currentIdx = allStatuses.indexOf(status);
                    return (
                      <div key={s} className="flex items-center gap-1 flex-1">
                        <div className={`w-2.5 h-2.5 rounded-full ${i <= currentIdx ? 'bg-teal-500' : 'bg-gray-200'}`} />
                        {i < allStatuses.length - 1 && <div className={`flex-1 h-px ${i < currentIdx ? 'bg-teal-400' : 'bg-gray-200'}`} />}
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
